import { motion } from 'framer-motion';
import { AnimatedLogo } from '@/components/AnimatedLogo';
import { NavLink } from '@/components/NavLink';

type AuthLayoutProps = {
  children: React.ReactNode;
  title: string;
  subtitle: string;
  backTo?: {
    label: string;
    path: string;
  };
};

export function AuthLayout({
  children,
  title,
  subtitle,
  backTo
}: AuthLayoutProps) {
  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row">
      {/* Left crystal ball animation panel - only visible on medium screens and up */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-purple-600/20 to-purple-900/30">
        <div className="w-full h-full flex items-center justify-center p-8 relative overflow-hidden">
          {/* Crystal ball container */}
          <div className="relative w-full max-w-md aspect-square">
            {/* Outer glow */}
            <motion.div
              className="absolute inset-0 rounded-full bg-purple-300/20"
              animate={{
                boxShadow: [
                  "0 0 40px 20px rgba(168, 85, 247, 0.3)",
                  "0 0 60px 30px rgba(168, 85, 247, 0.4)",
                  "0 0 40px 20px rgba(168, 85, 247, 0.3)"
                ],
                scale: [1, 1.05, 1]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{ borderRadius: '50%' }}
            />
            
            {/* Crystal ball outer sphere */}
            <motion.div
              className="absolute inset-4 rounded-full bg-purple-500/10 backdrop-blur-sm border border-white/20"
              animate={{
                borderRadius: [
                  '50%',
                  '50%',
                  '50%'
                ],
                boxShadow: [
                  "inset 0 0 20px rgba(255, 255, 255, 0.5), 0 0 20px rgba(168, 85, 247, 0.3)",
                  "inset 0 0 30px rgba(255, 255, 255, 0.6), 0 0 30px rgba(168, 85, 247, 0.4)",
                  "inset 0 0 20px rgba(255, 255, 255, 0.5), 0 0 20px rgba(168, 85, 247, 0.3)"
                ]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            
            {/* Crystal ball inner core - empty now, removed the 3 circular reflections */}
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Empty space where particles used to be */}
            </div>
            
            {/* Light rays */}
            <motion.div
              className="absolute inset-0 opacity-30"
              style={{
                background: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, rgba(0,0,0,0) 70%)',
              }}
              animate={{
                opacity: [0.2, 0.4, 0.2]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>
          
          {/* Background light flares */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-20 h-20 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.7) 0%, rgba(0,0,0,0) 70%)',
            }}
            animate={{
              opacity: [0.3, 0.7, 0.3]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          <motion.div
            className="absolute bottom-1/4 right-1/3 w-32 h-32 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(168, 85, 247, 0.6) 0%, rgba(0,0,0,0) 70%)',
            }}
            animate={{
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
        </div>
      </div>

      {/* Right content panel */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="px-6 py-4 flex items-center justify-between border-b border-border/40">
          <div className="flex items-center gap-2">
            <motion.span
              className="text-lg font-medium"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              Drimt
            </motion.span>
          </div>

          {backTo && (
            <NavLink to={backTo.path} className="text-sm text-muted-foreground hover:text-foreground">
              {backTo.label}
            </NavLink>
          )}
        </header>

        <main className="flex-1 flex items-center justify-center p-6">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="mb-8 text-center">
              <motion.h1
                className="text-2xl font-semibold mb-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {title}
              </motion.h1>
              <motion.p
                className="text-muted-foreground"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {subtitle}
              </motion.p>
            </div>

            {children}
          </motion.div>
        </main>

        <footer className="py-4 px-6 border-t border-border/40">
          <div className="text-center text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Drimt. All rights reserved.
          </div>
        </footer>
      </div>
    </div>
  );
}
